import {
  Button,
  CheckIcon,
  ChevronDownIcon,
  List,
  Popover,
  Typography,
} from '@iheartjane/reefer';
import type { LabeledOption } from '@iheartjane/types';
import { useCallback } from 'react';

import { useFilterSearchParams } from '@jane/bloom/hooks/filters';
import type { SortOptions } from '@jane/bloom/types/sort';
import { SORT_ORDER_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

import styles from './sortSelector.module.css';

interface SortSelectorProps {
  currentSort: SortOptions;
  sortOptions: readonly LabeledOption<SortOptions>[];
}

export const SortSelector = ({
  currentSort,
  sortOptions,
}: SortSelectorProps) => {
  const [, setSearchParams] = useFilterSearchParams();

  const changeSort = useCallback(
    async (value: string) => {
      setSearchParams((prev) => {
        prev.set(SORT_ORDER_QUERY_KEY, value);
        return prev;
      });
    },
    [setSearchParams]
  );

  const selectedSortLabel = `Sort: ${
    sortOptions.find((item) => item.value === currentSort)?.label
  }`;

  return (
    <div className={styles['productSortButton']}>
      <Popover
        target={
          <div className={styles['productFilterPopoverTarget']}>
            <Button
              variant="tertiary"
              endIcon={<ChevronDownIcon size="sm" />}
              label={selectedSortLabel}
              data-testid="sort-button"
            />
          </div>
        }
        label="Sort products"
        alignment={{ horizontal: 'right' }}
      >
        {({ closePopover }) => (
          <div data-testid="sort-list">
            <List label="Product sort options">
              {sortOptions.map(({ label, value }) => (
                <List.Item
                  aria-label={label}
                  data-testid={`${label}`}
                  ml={16}
                  key={value}
                  onClick={() => {
                    changeSort(value);
                    closePopover();
                  }}
                  className={styles['sortContainer']}
                >
                  <div className={styles['sortLabel']}>
                    <Typography className={styles['labelContainer']}>
                      {label}
                    </Typography>
                    <span className={styles['checkIconContainer']}>
                      {currentSort === value && <CheckIcon />}
                    </span>
                  </div>
                </List.Item>
              ))}
            </List>
          </div>
        )}
      </Popover>
    </div>
  );
};
